import {CommonModule} from '@angular/common';
import {Component, OnInit, inject} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {FormsModule} from '@angular/forms';
import {Router, RouterLink} from '@angular/router';
import {AdminApiService} from '../../admin-api.service';
import {AdminUser, SupportSession} from '../../admin.models';
import {AdminTranslatePipe} from '../../core/i18n/admin-translate.pipe';
import {AdminI18nService} from '../../core/i18n/admin-i18n.service';
import {UiButtonComponent, UiIconComponent, UiInputComponent, UiSelectComponent, UiToastService} from 'freyr-ui';

interface PermissionGroup {
  key: string;
  labelKey: string;
  rights: Array<'read' | 'write'>;
  variant: 'accent' | 'warning' | 'info' | 'success' | 'neutral';
}

@Component({
  selector: 'app-support-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, UiButtonComponent, UiInputComponent, UiSelectComponent, UiIconComponent, AdminTranslatePipe],
  templateUrl: './support-page.component.html',
  styleUrl: './support-page.component.scss'
})
export class SupportPageComponent implements OnInit {
  private readonly api = inject(AdminApiService);
  private readonly router = inject(Router);
  private readonly i18n = inject(AdminI18nService);
  private readonly toast = inject(UiToastService);

  sessions: SupportSession[] = [];
  currentUser: AdminUser | null = null;
  statusFilter = '';
  loadingSessions = false;
  closingSessionId: string | null = null;
  resumingSessionId: string | null = null;
  closeReasons: Record<string, string> = {};
  ngOnInit(): void {
    this.api.getCurrentUser().subscribe({
      next: (user) => {
        this.currentUser = user;
      }
    });
    this.loadSupportSessions();
  }

  loadSupportSessions(): void {
    this.loadingSessions = true;
    this.api.listSupportSessions(this.statusFilter).subscribe({
      next: (sessions) => {
        this.sessions = sessions ?? [];
        this.loadingSessions = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loadingSessions = false;
        const message = typeof error.error?.message === 'string' && error.error.message.trim()
          ? error.error.message.trim()
          : this.i18n.t('admin.support.loadError');
        this.toast.error(message);
      }
    });
  }

  closeSession(session: SupportSession): void {
    this.closingSessionId = session.id;
    this.api.closeSupportSession(session.id, this.closeReasons[session.id]).subscribe({
      next: () => {
        this.closingSessionId = null;
        this.toast.success(this.i18n.t('admin.support.closedSuccess', { id: session.id }));
        this.loadSupportSessions();
      },
      error: (error: HttpErrorResponse) => {
        this.closingSessionId = null;
        const message = typeof error.error?.message === 'string' && error.error.message.trim()
          ? error.error.message.trim()
          : this.i18n.t('admin.support.closeError');
        this.toast.error(message);
      }
    });
  }

  canResume(session: SupportSession): boolean {
    return session.status === 'ACTIVE'
      && (
        session.actorUserId === this.currentUser?.id
        || (this.currentUser?.roles?.includes('ADMIN') ?? false)
      );
  }

  resumeSession(session: SupportSession): void {
    if (!this.canResume(session)) {
      return;
    }
    this.resumingSessionId = session.id;
    this.api.resumeSupportSession(session.id).subscribe({
      next: (response) => {
        this.resumingSessionId = null;
        if (response?.redirectUrl) {
          const popup = window.open(response.redirectUrl, '_blank', 'noopener,noreferrer');
          if (!popup) {
            window.location.href = response.redirectUrl;
          }
        }
      },
      error: (error: HttpErrorResponse) => {
        this.resumingSessionId = null;
        const message = typeof error.error?.message === 'string' && error.error.message.trim()
          ? error.error.message.trim()
          : this.i18n.t('admin.support.resumeError');
        this.toast.error(message);
      }
    });
  }

  openAudit(session: SupportSession): void {
    void this.router.navigate(['/audit'], {
      queryParams: {
        query: session.id
      }
    });
  }

  groupedPermissions(permissions: string[]): PermissionGroup[] {
    const groups = new Map<string, PermissionGroup>();

    for (const permission of this.sortedPermissions(permissions)) {
      const groupKey = this.permissionGroupKey(permission);
      const current = groups.get(groupKey) ?? {
        key: groupKey,
        labelKey: `admin.permissions.groups.${groupKey}`,
        rights: [],
        variant: this.permissionVariant(permission)
      };

      const rightLabel: 'read' | 'write' = permission.endsWith('_READ') ? 'read' : 'write';
      if (!current.rights.includes(rightLabel)) {
        current.rights.push(rightLabel);
      }
      groups.set(groupKey, current);
    }

    return Array.from(groups.values());
  }

  sortedPermissions(permissions: string[]): string[] {
    return [...permissions].sort((left, right) => this.permissionSortKey(left) - this.permissionSortKey(right));
  }

  permissionVariant(permission: string): 'accent' | 'warning' | 'info' | 'success' | 'neutral' {
    if (permission.startsWith('AUTO_MESSAGES')) {
      return 'accent';
    }
    if (permission.startsWith('MODERATION')) {
      return 'warning';
    }
    if (permission.startsWith('DISCORD')) {
      return 'info';
    }
    if (permission.startsWith('LEVELS')) {
      return 'accent';
    }
    if (permission.startsWith('BOT_COMMANDS')) {
      return 'success';
    }
    return 'neutral';
  }

  avatarInitial(label: string | null | undefined): string {
    const value = label?.trim();
    return value ? value.charAt(0).toUpperCase() : '?';
  }

  statusLabel(status: string): string {
    if (status === 'ACTIVE') return this.i18n.t('admin.common.active');
    if (status === 'CLOSED') return this.i18n.t('admin.common.closed');
    if (status === 'EXPIRED') return this.i18n.t('admin.common.expired');
    return status;
  }

  formatSupportDate(key: 'created' | 'expires' | 'lastExchange', value: string | null | undefined): string {
    const fallbackKey = key === 'lastExchange' ? 'admin.common.never' : 'admin.common.unknown';
    const date = value ? new Date(value).toLocaleString(this.i18n.currentLang === 'en' ? 'en-GB' : 'fr-FR') : this.i18n.t(fallbackKey);
    return this.i18n.t(`admin.support.${key}`, { date });
  }

  sessionOwnerHint(session: SupportSession): string | null {
    if (this.canResume(session) || session.status !== 'ACTIVE') {
      return null;
    }
    return this.i18n.t('admin.support.resumeRestricted');
  }

  private permissionSortKey(permission: string): number {
    const categoryOrder = this.permissionCategoryOrder(permission);
    const rightOrder = permission.endsWith('_READ') ? 0 : 1;
    return categoryOrder * 10 + rightOrder;
  }

  private permissionGroupKey(permission: string): string {
    if (permission.startsWith('AUTO_MESSAGES')) return 'AUTO_MESSAGES';
    if (permission.startsWith('BOT_COMMANDS')) return 'BOT_COMMANDS';
    if (permission.startsWith('DISCORD')) return 'DISCORD';
    if (permission.startsWith('MODERATION')) return 'MODERATION';
    if (permission.startsWith('TWITCH')) return 'TWITCH';
    if (permission.startsWith('LEVELS')) return 'LEVELS';
    return permission;
  }

  private permissionCategoryOrder(permission: string): number {
    if (permission.startsWith('AUTO_MESSAGES')) return 0;
    if (permission.startsWith('BOT_COMMANDS')) return 1;
    if (permission.startsWith('DISCORD')) return 2;
    if (permission.startsWith('MODERATION')) return 3;
    if (permission.startsWith('TWITCH')) return 4;
    if (permission.startsWith('LEVELS')) return 5;
    return 99;
  }
}

